import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronRight, Loader2, Save, Eye, EyeOff } from 'lucide-react';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { toast } from 'sonner';
import { PALETTE_GROUPS } from './sqs/palettes';
import { renderPreviewBlock } from './shared/SharedSectionRenderer';
import { resolveSiteTokens, SiteSurface } from './shared/siteTokens';
import { ScaledSiteFrame } from './shared/ScaledSiteFrame';
import { useCustomCssInjection } from './sqs/useCustomCssInjection';
import { ensureGridOverlayStyles } from './sqs/GridOverlay';
import { applyFluidOffsets } from './sqs/useFluidTextDrag';
import { useSiteChrome, SiteHeader, SiteFooter } from './site-chrome/SiteChrome';
import { LogoUploadInput } from './LogoUploadInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Iter 196b — SQUARESPACE FONT LIBRARY
// Squarespace's Design Palette ships ~60 curated Google Fonts. We import
// the same catalog here so every pairing, preset and manual font pick
// resolves to the same weight range and looks visually identical.
//
// Grouped by category to help vendors find them. Each entry maps to a
// Google Fonts family + weights we've pre-loaded via the CSS import
// injected below.
const FONT_LIBRARY = {
  serif: [
    'Playfair Display', 'Cormorant Garamond', 'EB Garamond', 'Libre Baskerville',
    'Lora', 'Merriweather', 'Fraunces', 'Bodoni Moda', 'DM Serif Display',
    'Alegreya', 'Crimson Text', 'Spectral', 'Cardo', 'PT Serif', 'Prata',
    'Cinzel', 'Bitter', 'Domine',
  ],
  sans: [
    'Inter', 'DM Sans', 'Manrope', 'Archivo', 'Work Sans', 'Poppins',
    'Open Sans', 'Montserrat', 'Karla', 'Fira Sans', 'Nunito Sans',
    'Josefin Sans', 'Barlow', 'Rubik', 'Lato', 'Source Sans 3',
    'Public Sans', 'Space Grotesk', 'Roboto', 'Outfit', 'Plus Jakarta Sans',
  ],
  display: [
    'Syne', 'Anton', 'Archivo Black', 'Bebas Neue', 'Oswald', 'Righteous',
    'Poiret One', 'Big Shoulders Display', 'Abril Fatface',
    'Yeseva One', 'Unbounded', 'Chakra Petch',
  ],
  handwritten: [
    'Caveat', 'Dancing Script', 'Playfair Display SC', 'Great Vibes',
    'Sacramento', 'Pacifico', 'Kalam', 'Amatic SC',
  ],
  mono: [
    'JetBrains Mono', 'Space Mono', 'IBM Plex Mono', 'Fira Code', 'Roboto Mono',
  ],
};

// Flatten for easy iteration
const ALL_FONTS = [
  ...FONT_LIBRARY.serif, ...FONT_LIBRARY.sans, ...FONT_LIBRARY.display,
  ...FONT_LIBRARY.handwritten, ...FONT_LIBRARY.mono,
];

// Squarespace-style curated font pairings. Each pair mimics one of their
// signature Design Palette combinations.
const FONT_PAIRS = [
  // Original set kept for backwards compatibility
  { id: 'syne-inter',            heading: 'Syne',                 body: 'Inter' },
  { id: 'playfair-lora',         heading: 'Playfair Display',     body: 'Lora' },
  { id: 'fraunces-manrope',      heading: 'Fraunces',             body: 'Manrope' },
  { id: 'cormorant-inter',       heading: 'Cormorant Garamond',   body: 'Inter' },
  { id: 'oswald-roboto',         heading: 'Oswald',               body: 'Roboto' },
  { id: 'archivo-manrope',       heading: 'Archivo Black',        body: 'Manrope' },
  { id: 'dmsans-dmsans',         heading: 'DM Sans',              body: 'DM Sans' },
  { id: 'bebas-inter',           heading: 'Bebas Neue',           body: 'Inter' },
  // Squarespace signature pairs
  { id: 'ebgaramond-worksans',   heading: 'EB Garamond',          body: 'Work Sans' },
  { id: 'dmserif-inter',         heading: 'DM Serif Display',     body: 'Inter' },
  { id: 'bodoni-karla',          heading: 'Bodoni Moda',          body: 'Karla' },
  { id: 'poppins-poppins',       heading: 'Poppins',              body: 'Poppins' },
  { id: 'anton-open',            heading: 'Anton',                body: 'Open Sans' },
  { id: 'abril-lato',            heading: 'Abril Fatface',        body: 'Lato' },
  { id: 'yeseva-manrope',        heading: 'Yeseva One',           body: 'Manrope' },
  { id: 'cinzel-lora',           heading: 'Cinzel',               body: 'Lora' },
  { id: 'spacegrotesk-inter',    heading: 'Space Grotesk',        body: 'Inter' },
  { id: 'unbounded-jakarta',     heading: 'Unbounded',            body: 'Plus Jakarta Sans' },
  { id: 'libre-source',          heading: 'Libre Baskerville',    body: 'Source Sans 3' },
  { id: 'prata-jakarta',         heading: 'Prata',                body: 'Plus Jakarta Sans' },
];

const BUTTON_STYLES = [
  { id: 'pill',    label: 'Pill',    radius: 999 },
  { id: 'rounded', label: 'Rounded', radius: 12 },
  { id: 'square',  label: 'Square',  radius: 0 },
];

const HEADER_LAYOUTS = [
  { id: 'logo-left',   label: 'Logo Left + Nav Right' },
  { id: 'logo-center', label: 'Logo Centered, Nav Below' },
  { id: 'logo-stack',  label: 'Stacked (logo top, nav under)' },
  { id: 'minimal',     label: 'Minimal (logo only)' },
];

const FOOTER_LAYOUTS = [
  { id: 'three-col',  label: 'Three Columns' },
  { id: 'mega',       label: 'Mega Footer (4 cols + newsletter)' },
  { id: 'minimal',    label: 'Minimal (logo + copyright)' },
  { id: 'centered',   label: 'Centered (links centered + social)' },
];

// Flatten palette groups into a single picker list
const ALL_PALETTES = PALETTE_GROUPS.flatMap(g => g.palettes.map(p => ({ ...p, group: g.group })));

export default function DashboardStyles() {
  const [site, setSite] = useState(null);
  const [pageBlocks, setPageBlocks] = useState([]);
  const [theme, setTheme] = useState({
    palette: 'fuchsia-violet',
    swatches: ['#FAFAFA', '#E5DDFF', '#A78BFA', '#8B5CF6', '#0E0E23'],
    fonts: 'syne-inter',
    heading_font: 'Syne',
    body_font: 'Inter',
    button_style: 'pill',
    header_layout: 'logo-left',
    header_sticky: true,
    footer_layout: 'three-col',
    footer_text: '© ' + new Date().getFullYear(),
    footer_show_social: true,
  });
  const [activeTab, setActiveTab] = useState('palette');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hidePreview, setHidePreview] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/builder/my-site`, { headers }).then(r => r.data).catch(() => null),
      axios.get(`${API}/pages/home`, { headers }).then(r => r.data).catch(() => null),
    ]).then(([s, page]) => {
      if (s) {
        setSite(s);
        if (s.theme) setTheme(t => ({ ...t, ...s.theme }));
      }
      if (page?.blocks) setPageBlocks(page.blocks);
    }).finally(() => setLoading(false));
  }, []);

  const setAndSave = async (partial) => {
    setTheme(t => ({ ...t, ...partial }));
    setSaving(true);
    try {
      await axios.put(`${API}/builder/theme`, partial, { headers });
    } catch {
      // Fail silently — the local state still updates the preview
    }
    setTimeout(() => setSaving(false), 300);
  };

  const publish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/theme`, theme, { headers });
      toast.success('Styles published');
    } catch {
      toast.error('Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-3 text-[#9CA3AF]">
        <Loader2 className="animate-spin" size={16} /> Loading styles…
      </div>
    );
  }

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Website"
        title="Styles"
        subtitle="Themes, fonts, colors, buttons, header & footer — applied across your entire site."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setHidePreview(p => !p)}
              className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#9CA3AF] hover:text-white px-3 py-2 rounded-md flex items-center gap-1.5"
              data-testid="styles-toggle-preview"
            >
              {hidePreview ? <Eye size={12} /> : <EyeOff size={12} />} {hidePreview ? 'Show Preview' : 'Hide Preview'}
            </button>
            <SqsButton variant="primary" size="sm" icon={Save} onClick={publish} data-testid="styles-publish" disabled={saving}>
              {saving ? 'Saving…' : 'Publish'}
            </SqsButton>
          </div>
        }
      />

      <div className={`flex h-[calc(100vh-130px)] ${hidePreview ? '' : ''}`}>
        {/* LEFT — controls */}
        <div className={`${hidePreview ? 'w-full' : 'w-1/4 min-w-[340px]'} border-r border-white/[0.06] overflow-y-auto sqs-scrollbar`}>
          {/* Tabs */}
          <div className="px-4 pt-4 flex gap-1 sticky top-0 bg-[#0E0E23] z-10 flex-wrap">
            {[
              { id: 'presets', label: 'Presets' },
              { id: 'palette', label: 'Colors' },
              { id: 'fonts',   label: 'Fonts' },
              { id: 'header',  label: 'Header' },
              { id: 'footer',  label: 'Footer' },
              { id: 'buttons', label: 'Buttons' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-bold rounded transition-all ${
                  activeTab === t.id ? 'bg-[#A78BFA]/15 text-[#A78BFA]' : 'text-[#9CA3AF] hover:text-white'
                }`}
                data-testid={`styles-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-4 space-y-3">
            {activeTab === 'presets' && (
              <PresetsSection setAndSave={setAndSave} theme={theme} />
            )}
            {activeTab === 'palette' && (
              <PaletteSection theme={theme} setAndSave={setAndSave} />
            )}
            {activeTab === 'fonts' && (
              <FontsSection theme={theme} setAndSave={setAndSave} />
            )}
            {activeTab === 'header' && (
              <HeaderSection theme={theme} setAndSave={setAndSave} />
            )}
            {activeTab === 'footer' && (
              <FooterSection theme={theme} setAndSave={setAndSave} />
            )}
            {activeTab === 'buttons' && (
              <ButtonsSection theme={theme} setAndSave={setAndSave} />
            )}
          </div>
        </div>

        {/* RIGHT — live preview (75%) */}
        {!hidePreview && (
          <div className="flex-1 bg-[#0E0E23] overflow-y-auto sqs-scrollbar" data-testid="styles-live-preview">
            <LivePreview blocks={pageBlocks} theme={theme} site={site} />
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────── Sub-sections ───────────────────────────

// ── PRESETS — one-click design themes ────────────────────────────────────
const DESIGN_PRESETS = [
  {
    id: 'modern',
    label: 'Modern',
    tagline: 'Bold serif + clean sans-serif',
    swatch: ['#0E0E23', '#8B5CF6', '#F5F3FF'],
    apply: { palette: 'fuchsia-violet', fonts: 'syne-inter', heading_font: 'Syne', body_font: 'Inter', button_style: 'pill' },
  },
  {
    id: 'minimal',
    label: 'Minimal',
    tagline: 'Whitespace + subtle type',
    swatch: ['#FFFFFF', '#111111', '#F5F5F5'],
    apply: { palette: 'ivory-charcoal', fonts: 'dmsans-dmsans', heading_font: 'DM Sans', body_font: 'DM Sans', button_style: 'rounded' },
  },
  {
    id: 'editorial',
    label: 'Editorial',
    tagline: 'Fashion magazine feel',
    swatch: ['#FAF6EE', '#0E0E23', '#B8825E'],
    apply: { palette: 'cream-noir', fonts: 'playfair-lora', heading_font: 'Playfair Display', body_font: 'Lora', button_style: 'square' },
  },
  {
    id: 'bold',
    label: 'Bold',
    tagline: 'High contrast, large type',
    swatch: ['#000000', '#FFEB3B', '#F8F8F8'],
    apply: { palette: 'noir-yellow', fonts: 'archivo-manrope', heading_font: 'Archivo Black', body_font: 'Manrope', button_style: 'square' },
  },
  {
    id: 'playful',
    label: 'Playful',
    tagline: 'Rounded, colourful, friendly',
    swatch: ['#FEF3C7', '#EC4899', '#0E0E23'],
    apply: { palette: 'coral-cream', fonts: 'fraunces-manrope', heading_font: 'Fraunces', body_font: 'Manrope', button_style: 'pill' },
  },
  {
    id: 'luxury',
    label: 'Luxury',
    tagline: 'Refined serif, deep tones',
    swatch: ['#0E0E23', '#B8825E', '#F5F0E8'],
    apply: { palette: 'noir-gold', fonts: 'cormorant-inter', heading_font: 'Cormorant Garamond', body_font: 'Inter', button_style: 'rounded' },
  },
];

function PresetsSection({ setAndSave, theme = {} }) {
  const pd = theme.premium_defaults || {};
  const toggleAlternating = (v) => {
    setAndSave({ premium_defaults: { ...pd, alternating_bands: v } });
    toast.success(v ? 'Alternating bands enabled' : 'Alternating bands disabled');
  };
  return (
    <div className="space-y-3">
      <Card title="Design presets" testid="preset-picker">
        <p className="text-[11px] text-white/50 mb-3">
          Instant one-click themes. Fine-tune colors, fonts, and layout in the other tabs after picking.
        </p>
        <div className="grid grid-cols-1 gap-2.5">
          {DESIGN_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                setAndSave(p.apply);
                toast.success(`Applied "${p.label}" preset`);
              }}
              className="group relative flex items-center gap-3 p-3 rounded-xl border border-white/8 bg-white/[0.02] hover:border-[#A78BFA]/40 hover:bg-white/[0.04] transition-all text-left"
              data-testid={`preset-${p.id}`}
            >
              <div className="flex gap-0.5 shrink-0">
                {p.swatch.map((c) => (
                  <span key={c} className="w-6 h-8 rounded" style={{ background: c }} />
                ))}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm">{p.label}</p>
                <p className="text-white/45 text-[11px]">{p.tagline}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#A78BFA] opacity-0 group-hover:opacity-100 transition-opacity">Apply</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Premium Defaults — the Squarespace/Wix rhythm layer */}
      <Card title="Premium defaults" testid="premium-defaults-card">
        <p className="text-[11px] text-white/50 mb-3">
          Site-wide layout rhythm. Applies to every page and section unless overridden per block.
        </p>
        <label className="flex items-start gap-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!pd.alternating_bands}
            onChange={(e) => toggleAlternating(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#A78BFA]"
            data-testid="premium-alternating-bands"
          />
          <div>
            <p className="text-white text-[12.5px] font-bold">Alternating section bands</p>
            <p className="text-white/50 text-[11px]">Every other section switches background (light → cream → light → ivory) for editorial rhythm.</p>
          </div>
        </label>
        <label className="flex items-start gap-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={pd.editorial_spacing !== false}
            onChange={(e) => setAndSave({ premium_defaults: { ...pd, editorial_spacing: e.target.checked } })}
            className="mt-1 w-4 h-4 accent-[#A78BFA]"
            data-testid="premium-editorial-spacing"
          />
          <div>
            <p className="text-white text-[12.5px] font-bold">Editorial section padding (default)</p>
            <p className="text-white/50 text-[11px]">Every new section starts with 140/80 padding — Squarespace default.</p>
          </div>
        </label>
        <label className="flex items-start gap-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={pd.editorial_headlines !== false}
            onChange={(e) => setAndSave({ premium_defaults: { ...pd, editorial_headlines: e.target.checked } })}
            className="mt-1 w-4 h-4 accent-[#A78BFA]"
            data-testid="premium-editorial-headlines"
          />
          <div>
            <p className="text-white text-[12.5px] font-bold">Editorial-scale headlines</p>
            <p className="text-white/50 text-[11px]">Hero H1 scales to 96–128px on desktop with tight tracking.</p>
          </div>
        </label>
      </Card>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-[#15152E] border border-white/[0.06] rounded-xl p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-3">{title}</p>
      {children}
    </div>
  );
}

function PaletteSection({ theme, setAndSave }) {
  return (
    <Card title="Color Palette" testid="styles-card-palette">
      {PALETTE_GROUPS.map(group => (
        <div key={group.group} className="mb-4">
          <p className="text-[10px] tracking-[0.14em] uppercase text-white/50 font-bold mb-2 mt-1">{group.group}</p>
          <div className="space-y-1.5">
            {group.palettes.map(p => {
              const on = theme.palette === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setAndSave({ palette: p.id, swatches: p.swatches, accent_color: p.swatches[3] || p.swatches[2] })}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                    on ? 'border-[#A78BFA] bg-[#A78BFA]/10' : 'border-white/[0.06] hover:border-white/[0.18]'
                  }`}
                  data-testid={`styles-palette-${p.id}`}
                >
                  <div className="flex gap-1">
                    {p.swatches.map((s, i) => (
                      <div key={i} className="w-4 h-7 rounded-sm" style={{ background: s }} />
                    ))}
                  </div>
                  <p className="text-[12px] font-semibold text-white flex-1 text-left">{p.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Card>
  );
}

function FontsSection({ theme, setAndSave }) {
  const [tab, setTab] = React.useState('pairs');
  const [category, setCategory] = React.useState('serif');
  const currentHeading = theme.heading_font || 'Syne';
  const currentBody = theme.body_font || 'Inter';

  return (
    <>
      {/* Sub-tabs: Pairs | Heading | Body */}
      <div className="mb-3 grid grid-cols-3 gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
        {[
          { id: 'pairs',   label: 'Pairs'   },
          { id: 'heading', label: 'Heading' },
          { id: 'body',    label: 'Body'    },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-2 py-1.5 rounded text-[10.5px] uppercase tracking-wider font-bold transition-all ${
              tab === t.id ? 'bg-[#A78BFA] text-[#0E0E23]' : 'text-white/60 hover:text-white'
            }`}
            data-testid={`styles-font-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pairs' && (
        <Card title="Font Pairs" testid="styles-card-fonts">
          <p className="text-[11px] text-white/50 mb-3">Squarespace-style curated pairings. Click to apply both fonts at once.</p>
          <div className="space-y-2">
            {FONT_PAIRS.map(f => {
              const on = theme.fonts === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setAndSave({ fonts: f.id, heading_font: f.heading, body_font: f.body })}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg border transition-all ${
                    on ? 'border-[#A78BFA] bg-[#A78BFA]/10' : 'border-white/[0.06] hover:border-white/[0.18]'
                  }`}
                  data-testid={`styles-font-${f.id}`}
                >
                  <div className="text-left">
                    <p className="text-white text-[18px] font-bold tracking-tight leading-tight" style={{ fontFamily: `'${f.heading}', serif` }}>
                      Aa Heading
                    </p>
                    <p className="text-[#9CA3AF] text-[11px] mt-0.5" style={{ fontFamily: `'${f.body}', sans-serif` }}>
                      {f.heading} / {f.body}
                    </p>
                  </div>
                  <ChevronRight size={13} className={on ? 'text-[#A78BFA]' : 'text-[#64748B]'} />
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {(tab === 'heading' || tab === 'body') && (
        <Card
          title={tab === 'heading' ? 'Heading font' : 'Body font'}
          testid={`styles-card-font-${tab}`}
        >
          <p className="text-[11px] text-white/50 mb-3">
            {tab === 'heading' ? 'Used for H1-H6, buttons, and eyebrows.' : 'Used for paragraphs, captions, and lists.'}
          </p>
          {/* Category browser */}
          <div className="mb-3 grid grid-cols-5 gap-1">
            {Object.keys(FONT_LIBRARY).map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-1.5 py-1.5 rounded text-[9.5px] uppercase tracking-wider font-bold border transition-all ${
                  category === cat
                    ? 'bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#A78BFA]'
                    : 'bg-white/[0.02] border-white/[0.08] text-white/60 hover:text-white'
                }`}
                data-testid={`styles-font-cat-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>
          {/* Font list — each shows a preview in its own family */}
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {FONT_LIBRARY[category].map(font => {
              const on = tab === 'heading' ? currentHeading === font : currentBody === font;
              return (
                <button
                  key={font}
                  onClick={() => setAndSave(tab === 'heading' ? { heading_font: font } : { body_font: font })}
                  onMouseEnter={() => window.dispatchEvent(new CustomEvent('wb:type-test-hover', { detail: { font, target: tab } }))}
                  onMouseLeave={() => window.dispatchEvent(new CustomEvent('wb:type-test-hover', { detail: null }))}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg border transition-all ${
                    on ? 'border-[#A78BFA] bg-[#A78BFA]/10' : 'border-white/[0.06] hover:border-white/[0.18]'
                  }`}
                  data-testid={`styles-font-${tab}-${font.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-white text-[19px] font-bold leading-tight truncate"
                       style={{ fontFamily: `'${font}', ${category === 'serif' ? 'serif' : category === 'mono' ? 'monospace' : 'sans-serif'}` }}>
                      Aa {font}
                    </p>
                    <p className="text-[#9CA3AF] text-[10.5px] mt-0.5 uppercase tracking-wider">{category}</p>
                  </div>
                  {on && <span className="text-[10px] uppercase tracking-wider font-bold text-[#A78BFA]">Active</span>}
                </button>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
}

function HeaderSection({ theme, setAndSave }) {
  return (
    <>
      <Card title="Header Logo" testid="styles-card-header-logo">
        <LogoUploadInput
          value={theme.header_logo_url || theme.logo_url || ''}
          onChange={(url) => setAndSave({ header_logo_url: url, logo_url: url })}
          label="Site logo"
          hint="Shown in the navigation"
          testId="styles-header-logo-upload"
        />
      </Card>
      <Card title="Header Layout" testid="styles-card-header-layout">
        <div className="space-y-2">
          {HEADER_LAYOUTS.map(l => {
            const on = theme.header_layout === l.id;
            return (
              <button
                key={l.id}
                onClick={() => setAndSave({ header_layout: l.id })}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                  on ? 'border-[#A78BFA] bg-[#A78BFA]/10 text-white' : 'border-white/[0.06] text-[#9CA3AF] hover:border-white/[0.18] hover:text-white'
                }`}
                data-testid={`styles-header-${l.id}`}
              >
                <p className="text-[13px] font-semibold">{l.label}</p>
              </button>
            );
          })}
        </div>
      </Card>
      <Card title="Header Behavior" testid="styles-card-header-behavior">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-[13px] text-white">Sticky on scroll</span>
          <input
            type="checkbox"
            checked={!!theme.header_sticky}
            onChange={(e) => setAndSave({ header_sticky: e.target.checked })}
            data-testid="styles-header-sticky"
            className="accent-[#A78BFA] w-4 h-4"
          />
        </label>
      </Card>
    </>
  );
}

function FooterSection({ theme, setAndSave }) {
  return (
    <>
      <Card title="Footer Logo" testid="styles-card-footer-logo">
        <LogoUploadInput
          value={theme.footer_logo_url || ''}
          onChange={(url) => setAndSave({ footer_logo_url: url })}
          label="Footer logo"
          hint="Shown in the footer (often white-on-dark)"
          testId="styles-footer-logo-upload"
        />
      </Card>
      <Card title="Footer Layout" testid="styles-card-footer-layout">
        <div className="space-y-2">
          {FOOTER_LAYOUTS.map(l => {
            const on = theme.footer_layout === l.id;
            return (
              <button
                key={l.id}
                onClick={() => setAndSave({ footer_layout: l.id })}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                  on ? 'border-[#A78BFA] bg-[#A78BFA]/10 text-white' : 'border-white/[0.06] text-[#9CA3AF] hover:border-white/[0.18] hover:text-white'
                }`}
                data-testid={`styles-footer-${l.id}`}
              >
                <p className="text-[13px] font-semibold">{l.label}</p>
              </button>
            );
          })}
        </div>
      </Card>
      <Card title="Footer Text" testid="styles-card-footer-text">
        <input
          value={theme.footer_text || ''}
          onChange={(e) => setAndSave({ footer_text: e.target.value })}
          placeholder="© 2026 Your Business"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#A78BFA]/40"
          data-testid="styles-footer-text"
        />
        <label className="mt-3 flex items-center justify-between cursor-pointer">
          <span className="text-[13px] text-white">Show social icons</span>
          <input
            type="checkbox"
            checked={!!theme.footer_show_social}
            onChange={(e) => setAndSave({ footer_show_social: e.target.checked })}
            data-testid="styles-footer-social"
            className="accent-[#A78BFA] w-4 h-4"
          />
        </label>
      </Card>
    </>
  );
}

function ButtonsSection({ theme, setAndSave }) {
  return (
    <>
      <Card title="Button Style" testid="styles-card-buttons">
        <div className="grid grid-cols-3 gap-2">
          {BUTTON_STYLES.map(b => {
            const on = theme.button_style === b.id;
            return (
              <button
                key={b.id}
                onClick={() => setAndSave({ button_style: b.id, button_radius: b.radius })}
                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-3 transition-all ${
                  on ? 'border-[#A78BFA] bg-[#A78BFA]/5' : 'border-white/[0.06] hover:border-white/[0.18]'
                }`}
                data-testid={`styles-button-${b.id}`}
              >
                <span
                  className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider"
                  style={{ borderRadius: b.radius, background: theme.swatches?.[3] || '#A78BFA', color: '#fff' }}
                >
                  Button
                </span>
                <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-white">{b.label}</span>
              </button>
            );
          })}
        </div>
      </Card>
      <Card title="Interaction Polish" testid="styles-card-polish">
        <p className="text-[11px] text-[#9CA3AF] mb-3 leading-relaxed">
          Add a soft glow under every button and an animated underline under every link site-wide.
        </p>
        <label className="flex items-center justify-between cursor-pointer mb-3">
          <span className="text-[13px] text-white">Button hover-glow</span>
          <input
            type="checkbox"
            checked={!!theme.hover_glow}
            onChange={(e) => setAndSave({ hover_glow: e.target.checked })}
            data-testid="styles-hover-glow"
            className="accent-[#A78BFA] w-4 h-4"
          />
        </label>
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-[13px] text-white">Link underline animation</span>
          <input
            type="checkbox"
            checked={!!theme.link_underline_anim}
            onChange={(e) => setAndSave({ link_underline_anim: e.target.checked })}
            data-testid="styles-link-underline"
            className="accent-[#A78BFA] w-4 h-4"
          />
        </label>
      </Card>
    </>
  );
}

// ─────────────────────────── Live Preview ───────────────────────────

export function LivePreview({ blocks, theme, site, pageId = 'home' }) {
  // Iter 197 — Type Test mode. Hovering a font in the picker temporarily
  // overrides the heading/body font in this preview.
  const [hoverOverride, setHoverOverride] = React.useState(null);
  React.useEffect(() => {
    const onHover = (e) => setHoverOverride(e.detail || null);
    window.addEventListener('wb:type-test-hover', onHover);
    return () => window.removeEventListener('wb:type-test-hover', onHover);
  }, []);
  const { header: siteChromeHeader, footer: siteChromeFooter } = useSiteChrome();
  const effectiveTheme = hoverOverride
    ? { ...theme,
        heading_font: hoverOverride.target === 'body' ? (theme.heading_font || 'Syne') : hoverOverride.font,
        body_font:    hoverOverride.target === 'heading' ? (theme.body_font || 'Inter') : hoverOverride.font,
      }
    : theme;
  // WYSIWYG — same tokens, same surface, same block renderer, same custom
  // CSS, same fluid offsets and same canvas width as the full-screen editor.
  const tokens = resolveSiteTokens(effectiveTheme, site?.settings);
  const { palette, headingFont, radius } = tokens;
  const brand = site?.business_name || site?.name || 'Your Brand';
  const surfaceRef = React.useRef(null);
  useCustomCssInjection({ pageId, enabled: true });
  React.useEffect(() => { ensureGridOverlayStyles(); }, []);
  React.useLayoutEffect(() => { applyFluidOffsets(surfaceRef.current, blocks, 'desktop'); });

  return (
    <div className="relative" data-testid="live-preview">
      {hoverOverride && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-black/70 text-white text-[10px] uppercase tracking-wider font-bold backdrop-blur pointer-events-none">
          Previewing {hoverOverride.font} — release to keep, click to apply
        </div>
      )}
      <ScaledSiteFrame>
        <SiteSurface tokens={tokens} className="pointer-events-none select-none" data-testid="live-preview-surface">
          <div ref={surfaceRef}>
            {siteChromeHeader
              ? <SiteHeader header={siteChromeHeader} />
              : <PreviewHeader theme={effectiveTheme} brand={brand} palette={palette} headingFont={headingFont} radius={radius} />}
            {(blocks && blocks.length > 0) ? blocks.map((b, i) => (
              <PreviewBlock key={b.id || i} block={b} palette={palette} headingFont={headingFont} radius={radius} />
            )) : (
              <PreviewEmpty />
            )}
            {siteChromeFooter
              ? <SiteFooter footer={siteChromeFooter} />
              : <PreviewFooter theme={effectiveTheme} brand={brand} palette={palette} />}
          </div>
        </SiteSurface>
      </ScaledSiteFrame>
    </div>
  );
}

function PreviewEmpty() {
  return (
    <div className="px-10 py-20 text-center">
      <p className="text-sm opacity-60">No blocks on this page yet. Visit Pages → Home to add content.</p>
    </div>
  );
}

export function PreviewHeader({ theme, brand, palette, headingFont, radius }) {
  const [bg, , accent, deep, ink] = palette;
  const layout = theme.header_layout || 'logo-left';
  const sticky = theme.header_sticky;
  const headerLogo = theme.header_logo_url || theme.logo_url || '';
  const navLinks = (theme.nav_links && theme.nav_links.length)
    ? theme.nav_links.map(l => l.label).filter(Boolean)
    : ['Home', 'About', 'Services', 'Shop', 'Contact'];
  const cls = `px-8 py-5 border-b ${sticky ? 'sticky top-0 z-10' : ''}`;
  const style = { background: bg, borderColor: ink + '20', color: ink };
  const logoStyle = { fontFamily: `'${headingFont}', sans-serif`, fontWeight: 700 };
  const LogoEl = headerLogo
    ? <img src={headerLogo} alt={brand} className="h-9 w-auto max-w-[180px] object-contain" />
    : <p className="text-2xl" style={logoStyle}>{brand}</p>;
  if (layout === 'logo-center') {
    return (
      <header className={cls} style={style} data-testid="preview-header">
        <div className="flex justify-center">{LogoEl}</div>
        <nav className="mt-4 flex justify-center gap-7 text-sm">
          {navLinks.map(l => <span key={l} className="opacity-80 hover:opacity-100 cursor-pointer">{l}</span>)}
        </nav>
      </header>
    );
  }
  if (layout === 'logo-stack') {
    return (
      <header className={cls} style={style} data-testid="preview-header">
        {LogoEl}
        <nav className="mt-3 flex gap-7 text-sm">
          {navLinks.map(l => <span key={l} className="opacity-80 hover:opacity-100 cursor-pointer">{l}</span>)}
        </nav>
      </header>
    );
  }
  if (layout === 'minimal') {
    return (
      <header className={cls + ' flex items-center justify-between'} style={style} data-testid="preview-header">
        {LogoEl}
      </header>
    );
  }
  // logo-left (default)
  return (
    <header className={cls + ' flex items-center justify-between'} style={style} data-testid="preview-header">
      {LogoEl}
      <nav className="flex items-center gap-7 text-sm">
        {navLinks.map(l => <span key={l} className="opacity-80 hover:opacity-100 cursor-pointer">{l}</span>)}
        <span className="px-4 py-2 text-xs font-bold tracking-wider uppercase" style={{ background: accent, color: '#fff', borderRadius: radius }}>Book</span>
      </nav>
    </header>
  );
}

export function PreviewFooter({ theme, brand, palette }) {
  const [bg, , accent, deep, ink] = palette;
  const layout = theme.footer_layout || 'three-col';
  const text = theme.footer_text || `© ${new Date().getFullYear()} ${brand}`;
  const showSocial = theme.footer_show_social !== false;
  const footerLogo = theme.footer_logo_url || '';
  const wrapStyle = { background: deep, color: '#fff' };
  const BrandEl = footerLogo
    ? <img src={footerLogo} alt={brand} className="h-10 w-auto max-w-[200px] object-contain mb-1" />
    : <p className="text-lg font-bold">{brand}</p>;

  if (layout === 'minimal') {
    return (
      <footer className="px-8 py-8 flex items-center justify-between" style={wrapStyle} data-testid="preview-footer">
        {footerLogo ? <img src={footerLogo} alt={brand} className="h-8 w-auto max-w-[160px] object-contain" /> : <p className="text-sm font-bold">{brand}</p>}
        <p className="text-xs opacity-70">{text}</p>
      </footer>
    );
  }
  if (layout === 'centered') {
    return (
      <footer className="px-8 py-12 text-center" style={wrapStyle} data-testid="preview-footer">
        <div className="flex justify-center">{BrandEl}</div>
        <nav className="mt-4 flex justify-center gap-6 text-sm opacity-80">
          {['About', 'Services', 'Contact'].map(l => <span key={l}>{l}</span>)}
        </nav>
        {showSocial && <p className="mt-4 opacity-60 text-xs tracking-[0.2em] uppercase">Instagram · TikTok · Facebook</p>}
        <p className="mt-6 text-xs opacity-50">{text}</p>
      </footer>
    );
  }
  if (layout === 'mega') {
    return (
      <footer className="px-8 py-14" style={wrapStyle} data-testid="preview-footer">
        {footerLogo && <img src={footerLogo} alt={brand} className="h-10 w-auto max-w-[200px] object-contain mb-6" />}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {['Shop', 'About', 'Support', 'Newsletter'].map(c => (
            <div key={c}>
              <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">{c}</p>
              <div className="space-y-1.5 text-sm opacity-90">
                <p>Link one</p><p>Link two</p><p>Link three</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs opacity-50 border-t border-white/15 pt-4">{text}</p>
      </footer>
    );
  }
  // three-col default
  return (
    <footer className="px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-8" style={wrapStyle} data-testid="preview-footer">
      <div>
        {BrandEl}
        {showSocial && <p className="mt-3 text-xs opacity-60 tracking-[0.2em] uppercase">Instagram · TikTok · Facebook</p>}
      </div>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">Explore</p>
        <div className="space-y-1.5 text-sm opacity-90"><p>Home</p><p>About</p><p>Shop</p><p>Contact</p></div>
      </div>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">Newsletter</p>
        <input className="w-full rounded px-3 py-2 text-xs bg-white/10 border border-white/15 text-white placeholder:text-white/40" placeholder="your@email.com" readOnly />
      </div>
      <p className="md:col-span-3 text-xs opacity-50 border-t border-white/15 pt-4">{text}</p>
    </footer>
  );
}

export function PreviewBlock({ block, palette, headingFont, radius, allProducts = [] }) {
  // Same renderer as the editor canvas (readOnly) → identical section DOM.
  if (!block || !block.type) return null;
  return renderPreviewBlock(block, { palette, headingFont, radius, allProducts });
}
